import { CONVEYOR_SPEED, CONVEYOR_MAX_ATTACKERS } from '../config/constants';

export interface Point {
  x: number;
  y: number;
}

export type ConveyorPath = Point[];

export interface AttackerEntry {
  attackerId: number;
  t: number;
  prevT: number; // t value at the start of this frame (before update)
  size: number; // how many conveyor slots this attacker occupies
  paused?: boolean; // true while entrance tween is playing — skip t advancement
  /** Seconds accumulated while paused. A stuck entrance animation (killed tween,
   *  occluded tab) is force-resumed by the scene via stalledPausedIds(). */
  pausedFor?: number;
}

/** Data returned for attackers that completed their lap this frame. */
export interface CompletedAttacker {
  attackerId: number;
  /** The t value at the start of this frame (before the final advance). */
  prevT: number;
}

export class ConveyorSystem {
  private path: ConveyorPath;
  private speed: number;
  private maxCapacity: number;
  private segmentLengths: number[];
  private totalLength: number;
  private attackers: AttackerEntry[];
  private _tempPoint: Point = { x: 0, y: 0 };
  private _cachedSlotsUsed = 0;

  private constructor(path: ConveyorPath, speed: number, maxCapacity: number) {
    this.path = path;
    this.speed = speed;
    this.maxCapacity = maxCapacity;
    this.attackers = [];

    this.segmentLengths = [];
    let total = 0;
    for (let i = 0; i < path.length - 1; i++) {
      const dx = path[i + 1].x - path[i].x;
      const dy = path[i + 1].y - path[i].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      this.segmentLengths.push(len);
      total += len;
    }
    this.totalLength = total;
  }

  static createOvalPath(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    segments: number = 64,
  ): ConveyorSystem {
    const path: ConveyorPath = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      path.push({
        x: cx + rx * Math.cos(angle),
        y: cy + ry * Math.sin(angle),
      });
    }
    return new ConveyorSystem(path, CONVEYOR_SPEED, CONVEYOR_MAX_ATTACKERS);
  }

  /**
   * Creates a rounded rectangle (racetrack) path.
   * @param left   Left edge x
   * @param top    Top edge y
   * @param right  Right edge x
   * @param bottom Bottom edge y
   * @param cornerRadius Radius of rounded corners
   * @param arcSegments  Points per quarter-circle arc
   */
  static createRoundedRectPath(
    left: number,
    top: number,
    right: number,
    bottom: number,
    cornerRadius: number = 20,
    arcSegments: number = 12,
  ): ConveyorSystem {
    const r = Math.min(cornerRadius, (right - left) / 2, (bottom - top) / 2);
    const path: ConveyorPath = [];

    // Corner centers
    const tlX = left + r, tlY = top + r;
    const trX = right - r, trY = top + r;
    const brX = right - r, brY = bottom - r;
    const blX = left + r, blY = bottom - r;

    // Helper: add arc points from startAngle to endAngle around (cx, cy)
    const addArc = (cx: number, cy: number, startAngle: number, endAngle: number) => {
      for (let i = 0; i <= arcSegments; i++) {
        const angle = startAngle + (endAngle - startAngle) * (i / arcSegments);
        path.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
      }
    };

    // Start at bottom-left, go clockwise: right -> up -> left -> down

    // Bottom edge (left to right)
    path.push({ x: left + r, y: bottom });
    path.push({ x: right - r, y: bottom });

    // Bottom-right arc (bottom to right, pi/2 to 0)
    addArc(brX, brY, Math.PI / 2, 0);

    // Right edge (bottom to top)
    path.push({ x: right, y: bottom - r });
    path.push({ x: right, y: top + r });

    // Top-right arc (right to top, 0 to -pi/2)
    addArc(trX, trY, 0, -Math.PI / 2);

    // Top edge (right to left)
    path.push({ x: right - r, y: top });
    path.push({ x: left + r, y: top });

    // Top-left arc (top to left, -pi/2 to -pi)
    addArc(tlX, tlY, -Math.PI / 2, -Math.PI);

    // Left edge (top to bottom)
    path.push({ x: left, y: top + r });
    path.push({ x: left, y: bottom - r });

    // Bottom-left arc (left to bottom, pi to pi/2)
    addArc(blX, blY, Math.PI, Math.PI / 2);

    // Close the path by returning to start
    path.push({ x: left + r, y: bottom });

    return new ConveyorSystem(path, CONVEYOR_SPEED, CONVEYOR_MAX_ATTACKERS);
  }

  getPath(): ConveyorPath {
    return this.path;
  }

  getStartPosition(): Point {
    return { ...this.path[0] };
  }

  getTotalLength(): number {
    return this.totalLength;
  }

  getSpeed(): number {
    return this.speed;
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  getPositionAtT(t: number): Point {
    // Wrap t into [0, 1)
    t = ((t % 1) + 1) % 1;

    const targetDist = t * this.totalLength;
    let accumulated = 0;

    for (let i = 0; i < this.segmentLengths.length; i++) {
      const segLen = this.segmentLengths[i];
      if (accumulated + segLen >= targetDist) {
        const segT = segLen === 0 ? 0 : (targetDist - accumulated) / segLen;
        const p0 = this.path[i];
        const p1 = this.path[i + 1];
        this._tempPoint.x = p0.x + (p1.x - p0.x) * segT;
        this._tempPoint.y = p0.y + (p1.y - p0.y) * segT;
        return this._tempPoint;
      }
      accumulated += segLen;
    }

    // Fallback: return last point
    const last = this.path[this.path.length - 1];
    this._tempPoint.x = last.x;
    this._tempPoint.y = last.y;
    return this._tempPoint;
  }

  /**
   * Returns the rotation angle (radians) an attacker at parameter t should
   * have so that it faces toward the centre of the conveyor belt.
   *
   * For a clockwise path the inward normal is the tangent rotated -90°.
   */
  getAngleAtT(t: number): number {
    t = ((t % 1) + 1) % 1;

    const targetDist = t * this.totalLength;
    let accumulated = 0;

    for (let i = 0; i < this.segmentLengths.length; i++) {
      const segLen = this.segmentLengths[i];
      if (accumulated + segLen >= targetDist) {
        const p0 = this.path[i];
        const p1 = this.path[i + 1];
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        // tangent angle along the segment
        const tangent = Math.atan2(dy, dx);
        // inward normal for clockwise path = tangent - π/2
        return tangent - Math.PI / 2;
      }
      accumulated += segLen;
    }

    // Fallback: use last segment direction
    const n = this.path.length;
    const dx = this.path[n - 1].x - this.path[n - 2].x;
    const dy = this.path[n - 1].y - this.path[n - 2].y;
    return Math.atan2(dy, dx) - Math.PI / 2;
  }

  /** Total conveyor slots currently in use. */
  slotsUsed(): number {
    return this._cachedSlotsUsed;
  }

  /** Whether an attacker of the given size can be added without exceeding capacity. */
  canAdd(size: number = 1): boolean {
    return this.slotsUsed() + size <= this.maxCapacity;
  }

  /**
   * Add an attacker entry to the belt.
   *
   * `force` bypasses the capacity check: the LOGICAL model (OrderedFlowEngine)
   * is the only capacity authority — its ring excludes riders it already culled
   * at dispatch, while this belt keeps them until their kill animations land,
   * so the belt can legitimately need to render more than `maxCapacity` for a
   * moment. A dispatch the model accepted must ALWAYS get a belt entry;
   * refusing one strands the rider in the model's ring with no lap animation
   * to ever settle it (the 2026-09 fire-station soft-lock).
   */
  addAttacker(attackerId: number, size: number = 1, paused: boolean = false, force: boolean = false): boolean {
    if (!force && !this.canAdd(size)) {
      return false;
    }
    this.attackers.push({ attackerId, t: 0, prevT: 0, size, paused });
    this._cachedSlotsUsed += size;
    return true;
  }

  /** Un-pause an attacker so it begins advancing along the conveyor. */
  resumeAttacker(attackerId: number): void {
    const entry = this.attackers.find((a) => a.attackerId === attackerId);
    if (entry) {
      entry.paused = false;
      entry.pausedFor = 0;
    }
  }

  /**
   * Entries still paused after `thresholdSeconds` of belt time — their entrance
   * animation was skipped or interrupted (killed tween, occluded tab) and their
   * `resumeAttacker` callback will never fire. The scene force-resumes these so
   * a broken animation can never hold the logical ring hostage.
   */
  stalledPausedIds(thresholdSeconds: number): number[] {
    const out: number[] = [];
    for (const a of this.attackers) {
      if (a.paused && (a.pausedFor ?? 0) >= thresholdSeconds) out.push(a.attackerId);
    }
    return out;
  }

  removeAttacker(attackerId: number): void {
    const idx = this.attackers.findIndex((a) => a.attackerId === attackerId);
    if (idx !== -1) {
      this._cachedSlotsUsed -= this.attackers[idx].size;
      this.attackers.splice(idx, 1);
    }
  }

  update(deltaSeconds: number): CompletedAttacker[] {
    const distanceMoved = this.speed * deltaSeconds;
    const tDelta = distanceMoved / this.totalLength;
    const completed: CompletedAttacker[] = [];

    let writeIdx = 0;
    for (let i = 0; i < this.attackers.length; i++) {
      const attacker = this.attackers[i];
      attacker.prevT = attacker.t;
      if (!attacker.paused) {
        attacker.t += tDelta;
      } else {
        attacker.pausedFor = (attacker.pausedFor ?? 0) + deltaSeconds;
      }
      if (attacker.t >= 1.0) {
        completed.push({ attackerId: attacker.attackerId, prevT: attacker.prevT });
        this._cachedSlotsUsed -= attacker.size;
      } else {
        this.attackers[writeIdx++] = attacker;
      }
    }
    this.attackers.length = writeIdx;

    return completed;
  }

  getAttackerPositions(): readonly AttackerEntry[] {
    return this.attackers;
  }

  /** Number of attacker units on the conveyor. */
  attackerCount(): number {
    return this.attackers.length;
  }
}
